import * as React from "react";
import { useEffect, useRef, useState } from "react";
import { type Course } from "../../providers/coursesProvider";
import { splitCompositeId } from "../../backoffice.utils";
import { DateTime } from "luxon";
import { Button, CircularProgress, Typography, useTheme } from "@mui/material";
import { useFormContext, useWatch } from "react-hook-form";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { v4 as randomUuid } from "uuid";
import { serverAPI } from "../../api/server.api";
import { useQueryClient } from "react-query";
import { TinyColor } from "@ctrl/tinycolor";
import { type CoursePlanningFormValues } from "./CoursePlanningForm";
import { getScheduledUeForModule } from "./getScheduledUeForModule";

export function FreeSlot(props: {
  course: Course;
  start: DateTime;
  end: DateTime;
  onPreviewHoverChange?: (hovered: boolean) => void;
}) {
  const { course, start, end, onPreviewHoverChange } = props;
  const theme = useTheme();
  const { trigger: triggerFormValidation, getValues } = useFormContext<CoursePlanningFormValues>();
  const [creatingCourseLesson, setCreatingCourseLesson] = useState(false);
  const [courseLessonCreated, setCourseLessonCreated] = useState(false);
  const queryClient = useQueryClient();
  const module = useWatch<CoursePlanningFormValues, "module">({ name: "module" });
  const moduleColor = module ? new TinyColor(module.color) : null;
  const [hovered, setHovered] = useState<boolean>(false);
  const previewHoverIsActiveRef = useRef(false);
  const hoveredOrCreatingCourseLesson = hovered || creatingCourseLesson;

  useEffect(() => {
    return () => {
      if (previewHoverIsActiveRef.current) {
        onPreviewHoverChange?.(false);
        previewHoverIsActiveRef.current = false;
      }
    };
  }, [onPreviewHoverChange]);

  if (courseLessonCreated) {
    return null;
  }

  const title = hoveredOrCreatingCourseLesson && module && moduleColor?.isValid ? module.name : undefined;
  const titleColor =
    hoveredOrCreatingCourseLesson && moduleColor?.isValid
      ? moduleColor.getBrightness() > 150
        ? new TinyColor(theme.palette.common.black).setAlpha(0.5).toRgbString()
        : new TinyColor(theme.palette.common.white).setAlpha(0.5).toRgbString()
      : theme.palette.text.disabled;

  return (
    <Button
      variant="text"
      sx={{ width: "100%", height: "100%", borderRadius: 0, display: "flex" }}
      className={hoveredOrCreatingCourseLesson && moduleColor?.isValid ? "pending-event" : undefined}
      onClick={async () => {
        if (creatingCourseLesson) {
          return;
        }
        const isValid = await triggerFormValidation();
        if (!isValid) {
          return;
        }
        const { module, lecturerId, roomId, noteForParticipants } = getValues() as AllDefined<CoursePlanningFormValues>;
        // Prevent scheduling if there are already enough course lessons for the selected module ...
        const ueScheduled = getScheduledUeForModule(course.events, module.name);
        const ueTotal = module.ue;
        if (ueScheduled >= ueTotal) {
          showErrorToast(`Es sind genügend Termine für ${module.name} geplant. Bitte wähle einen anderen Kursteil.`);
          return;
        }
        setCreatingCourseLesson(true);
        try {
          await serverAPI.createCalendarEvent({
            id: randomUuid(),
            type: "CourseLesson",
            courseId: course.id,
            courseModuleColor: module.color,
            courseModuleName: module.name,
            lecturerId,
            roomId: roomId && splitCompositeId(roomId)[1],
            start: start.toISO(),
            end: end.toISO(),
            noteForParticipants,
          });
          await queryClient.invalidateQueries();
          setCourseLessonCreated(true);
        } catch (error) {
          handleError("Fehler beim Anlegen des Termins.", error);
        } finally {
          setCreatingCourseLesson(false);
        }
      }}
      onMouseEnter={() => {
        setHovered(true);
        previewHoverIsActiveRef.current = true;
        onPreviewHoverChange?.(true);
      }}
      onMouseLeave={() => {
        setHovered(false);
        if (previewHoverIsActiveRef.current) {
          onPreviewHoverChange?.(false);
          previewHoverIsActiveRef.current = false;
        }
      }}
    >
      {hoveredOrCreatingCourseLesson && moduleColor?.isValid && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: -1,
            backgroundColor: moduleColor.setAlpha(0.5).toRgbString(),
          }}
        />
      )}
      {title && (
        <Typography
          variant="body2"
          fontWeight="bold"
          color={titleColor}
          sx={{
            position: "absolute",
            top: "3px",
            left: "3px",
          }}
          noWrap
        >
          {title}
        </Typography>
      )}
      <div style={{ flex: 1, display: "flex", justifyContent: "center", alignItems: "center" }}>
        {creatingCourseLesson ? (
          <div style={{ transform: "scale(0.5)" }}>
            <CircularProgress />
          </div>
        ) : (
          <AddCircleOutlineIcon sx={{ color: "text.disabled" }} />
        )}
      </div>
    </Button>
  );
}
